import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar } from 'lucide-react';
import { CreateEventModal } from '@/components/Events/CreateEventModal';
import { EditEventModal } from '@/components/Events/EditEventModal';
import { EventCard } from '@/components/Events/EventCard';
import { useEvents } from '@/hooks/useEvents';
import { useAuth } from '@/hooks/useAuth';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import eventsHeroBg from '@/assets/events-hero-bg.jpg';

const Events = () => {
  const { user } = useAuth();
  const { events, loading, loadEvents, joinEvent, leaveEvent, openInMaps } = useEvents();
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleEditEvent = (event: any) => {
    setEditingEvent(event);
    setShowEditModal(true);
  };

  const handleDeleteEvent = (eventId: string) => {
    setDeletingEventId(eventId);
    setShowDeleteDialog(true);
  };

  const confirmDeleteEvent = async () => {
    if (!deletingEventId) return;

    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', deletingEventId);

      if (error) throw error;

      toast.success('Evento eliminato con successo');
      loadEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Errore nell\'eliminazione dell\'evento');
    } finally {
      setShowDeleteDialog(false);
      setDeletingEventId(null);
    }
  };

  const handleShareEvent = async (event: any) => {
    const eventUrl = `${window.location.origin}/app/events/${event.id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.title,
          text: `${event.description || 'Evento su AstroPet'} - ${event.location_name}`,
          url: eventUrl,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(eventUrl);
      toast.success('Link copiato negli appunti!');
    }
  };

  useEffect(() => {
    document.title = 'Eventi | AstroPet';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Eventi pet-friendly nella tua zona. Organizza e partecipa a incontri, passeggiate e attività con altri proprietari di animali.');
  }, []);

  return (
    <div 
      className="cosmic-background min-h-screen"
      style={{ backgroundImage: `url(${eventsHeroBg})` }}
    >
      <div className="max-w-6xl mx-auto p-4 pb-20 lg:pb-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold gradient-text mb-2 font-space flex items-center">
            <Calendar className="w-8 h-8 mr-3 text-primary" />
            Eventi
          </h1>
          <p className="text-muted-foreground">
            Organizza e partecipa a eventi pet-friendly nella tua zona
          </p>
        </div>
        <CreateEventModal onEventCreated={loadEvents} />
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="glass shadow-card border-white/10">
              <CardContent className="p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                  <div className="h-16 bg-muted rounded"></div>
                  <div className="h-8 bg-muted rounded w-full"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : events.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onJoin={joinEvent}
              onLeave={leaveEvent}
              onOpenInMaps={openInMaps}
              onEdit={handleEditEvent}
              onDelete={handleDeleteEvent}
              onShare={handleShareEvent}
              currentUserId={user?.id}
            />
          ))}
        </div>
      ) : (
        <Card className="glass shadow-card border-white/10">
          <CardContent className="text-center py-12">
            <Calendar className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nessun evento in programma</h3>
            <p className="text-muted-foreground mb-6">
              Sii il primo a organizzare un evento per la community!
            </p>
            <CreateEventModal onEventCreated={loadEvents} />
          </CardContent>
        </Card>
      )}

      <EditEventModal
        event={editingEvent}
        open={showEditModal}
        onOpenChange={setShowEditModal}
        onEventUpdated={loadEvents}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina Evento</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questo evento? 
              Questa azione non può essere annullata e tutti i partecipanti verranno rimossi.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteEvent}
              className="bg-destructive hover:bg-destructive/90"
            >
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
};

export default Events;